/**
 * HydraSourceNode - Config-driven Hydra source node
 *
 * Holds source config (function name + param args). Chain is built
 * by HydraRenderer via graph query — no payload propagation.
 */

import { HydraBaseNode } from './HydraBaseNode';
import type { HydraFunctionMeta } from '../HydraEndpoint';
import { Dependencies } from '@/nodes/Dependencies';

export interface HydraSourceConfig {
  /** The Hydra function name (e.g., 'osc', 'noise', 'shape') */
  hydraFn: string;
  /** Endpoint names in order they're passed to the Hydra function */
  fnArgs: string[];
  type: string;
}

/**
 * Base class for all Hydra source nodes.
 */
export class HydraSourceNode<T extends Record<string, any>> extends HydraBaseNode<T> {

  protected config: HydraSourceConfig;

  constructor(id: string, deps: Dependencies, config: HydraSourceConfig) {
    super(id, deps);
    this.config = config;
  }

  get fnArgs() {
    return this.config.fnArgs;
  }

  get hydraFn() {
    return this.config.hydraFn;
  }

  /** Return this node's metadata for graph-query chain building */
  getHydraMeta(): HydraFunctionMeta {
    if (this.config.type === 'Webcam') {
      return { newSource: { type: 'webcam' } };
    }
    return { source: true, name: this.hydraFn, args: this.createArgGetters(this.fnArgs) };
  }
}
